import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarIcon, Plus, X, Clock, AlertCircle } from "lucide-react";
import { toLocalIso as toIsoDate, fromLocalIso as fromIsoDate, formatLocalDay, localToday } from "@/lib/localDate";

const formatDay = (iso) => formatLocalDay(iso, { fallback: "—" });

function DateButton({ value, onChange, placeholder, testId, min }) {
  const minDate = (min && fromIsoDate(min)) || localToday();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-between gap-2 rounded-lg border border-input bg-white px-2.5 py-1.5 text-xs hover:border-neutral-400 transition w-full"
          data-testid={testId}
        >
          <span className={value ? "text-neutral-900" : "text-neutral-400"}>{value ? formatDay(value) : placeholder}</span>
          <CalendarIcon className="h-3.5 w-3.5 text-neutral-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={fromIsoDate(value)}
          onSelect={(d) => onChange(toIsoDate(d))}
          disabled={(date) => date < minDate}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default function AvailabilityEditor({
  unavailableRanges = [],
  scheduledPauseAt = "",
  onChangeRanges,
  onChangeScheduledPause,
}) {
  const [draft, setDraft] = useState({ start: "", end: "", reason: "" });
  const [scheduledDate, setScheduledDate] = useState(scheduledPauseAt ? scheduledPauseAt.slice(0, 10) : "");
  const [scheduledTime, setScheduledTime] = useState(scheduledPauseAt ? (scheduledPauseAt.slice(11, 16) || "18:00") : "18:00");

  const addRange = () => {
    if (!draft.start || !draft.end) return;
    if (draft.end <= draft.start) return;
    onChangeRanges([...unavailableRanges, {
      id: `tmp-${Date.now()}`,
      start: draft.start,
      end: draft.end,
      reason: draft.reason.trim(),
    }]);
    setDraft({ start: "", end: "", reason: "" });
  };

  const removeRange = (id) => {
    onChangeRanges(unavailableRanges.filter((r) => r.id !== id));
  };

  const applySchedule = () => {
    if (!scheduledDate) {
      onChangeScheduledPause("");
      return;
    }
    onChangeScheduledPause(`${scheduledDate}T${scheduledTime}:00`);
  };

  const clearSchedule = () => {
    setScheduledDate("");
    setScheduledTime("18:00");
    onChangeScheduledPause("");
  };

  return (
    <div className="space-y-5" data-testid="availability-editor">
      {/* Scheduled one-shot pause */}
      <div className="rounded-2xl border border-border p-4 bg-white">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="h-4 w-4 text-neutral-500" />
          <Label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Schedule pause</Label>
        </div>
        <p className="text-xs text-neutral-500 mb-3">
          Pick a date & time for this car to pause itself. Useful if you know it's going off the road soon.
        </p>
        <div className="grid grid-cols-[1fr_120px_auto] gap-2 items-center">
          <DateButton
            value={scheduledDate}
            onChange={setScheduledDate}
            placeholder="Pause on date"
            testId="scheduled-pause-date"
          />
          <Input
            type="time"
            value={scheduledTime}
            onChange={(e) => setScheduledTime(e.target.value)}
            className="rounded-lg text-xs h-[34px]"
            data-testid="scheduled-pause-time"
          />
          <Button
            type="button"
            size="sm"
            onClick={applySchedule}
            className="rounded-full h-[34px]"
            disabled={!scheduledDate}
            data-testid="scheduled-pause-apply"
          >
            Save
          </Button>
        </div>
        {scheduledPauseAt && (
          <div className="mt-3 inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200" data-testid="scheduled-pause-active-badge">
            <AlertCircle className="h-3 w-3" />
            Will pause on {formatDay(scheduledPauseAt)} at {scheduledPauseAt.slice(11, 16) || "18:00"}
            <button
              type="button"
              onClick={clearSchedule}
              className="ml-1 text-amber-700 hover:text-amber-900"
              data-testid="scheduled-pause-clear"
              aria-label="Clear scheduled pause"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>

      {/* Multi-range unavailability calendar */}
      <div className="rounded-2xl border border-border p-4 bg-white">
        <Label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Unavailable date ranges</Label>
        <p className="text-xs text-neutral-500 mb-3 mt-1">
          Block specific windows when the car cannot be rented. Customers won't be able to book overlapping dates.
        </p>

        {/* Existing ranges */}
        {unavailableRanges.length > 0 && (
          <ul className="space-y-2 mb-3">
            {unavailableRanges.map((r) => (
              <li
                key={r.id || r.start}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-neutral-50 border border-border text-sm"
                data-testid={`unavailable-range-${r.id || r.start}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-neutral-900 text-sm">
                    {formatDay(r.start)} → {formatDay(r.end)}
                  </div>
                  {r.reason && <div className="text-xs text-neutral-500 truncate">{r.reason}</div>}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeRange(r.id)}
                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 shrink-0 h-7 w-7"
                  data-testid={`remove-unavailable-${r.id}`}
                  aria-label="Remove range"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        {/* Add new range */}
        <div className="rounded-xl border border-dashed border-border p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-neutral-400">Start</Label>
              <DateButton
                value={draft.start}
                onChange={(v) => setDraft({ ...draft, start: v })}
                placeholder="Start date"
                testId="unavailable-start-input"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-neutral-400">End</Label>
              <DateButton
                value={draft.end}
                onChange={(v) => setDraft({ ...draft, end: v })}
                placeholder="End date"
                testId="unavailable-end-input"
                min={draft.start || undefined}
              />
            </div>
          </div>
          <Input
            value={draft.reason}
            onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
            placeholder="Reason (optional) — e.g. service, vacation, vehicle inspection"
            className="rounded-lg text-xs h-9"
            data-testid="unavailable-reason-input"
          />
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={addRange}
            disabled={!draft.start || !draft.end || draft.end <= draft.start}
            className="rounded-full w-full h-8"
            data-testid="add-unavailable-range"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add range
          </Button>
        </div>
      </div>
    </div>
  );
}
